/**
 * Small presentational pieces shared by Glance, Browse and the detail screens.
 *
 * Everything here draws numbers it was handed. Scaling a bar or a sparkline to
 * fit its box is presentation of a figure from `summary.json`, not derivation
 * of a new one - the number on screen is always the number in the file.
 */
import { useEffect, useRef, useState } from "preact/hooks";

import type { Summary } from "./contracts/index.js";
import { categoryColor, dateName, money, pctShort, signedMoney, signedPct } from "./format.js";

type CategoryMap = Summary["months"][number]["by_category"];
type CategoryPctMap = NonNullable<Summary["months"][number]["by_category_pct"]>;

/**
 * The by-category split as stacked colour, plus a legend of the non-zero
 * slices, largest first. Ordering is filtering, not arithmetic - and so are
 * the percentages: `pct` is the scope's own `*_pct` map, computed by
 * publish.py because the client must not divide.
 */
export function CategorySplit({
  categories,
  currency,
  pct,
}: {
  categories: CategoryMap;
  currency: string;
  pct?: CategoryPctMap;
}) {
  const slices = (Object.entries(categories) as [string, number][])
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  if (!slices.length) return <p class="muted small">No category data.</p>;
  const max = slices[0]![1];
  return (
    <div class="catsplit">
      {slices.map(([name, value]) => (
        <div class="catrow" key={name}>
          <span class="dot" style={{ background: categoryColor(name) }} />
          <span class="label">{name}</span>
          <span class="bar-track">
            <span
              class="bar"
              style={{ width: `${(value / max) * 100}%`, background: categoryColor(name) }}
            />
          </span>
          <span class="value">
            {money(value, currency)}
            {pct && pct[name as keyof CategoryPctMap] !== undefined && (
              <span class="pctv"> · {pctShort(pct[name as keyof CategoryPctMap])}</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * The comparison a list row carries (BUILD-SPEC 8). `delta` and `delta_pct`
 * come off the row itself; when the file says null - no prior month - the
 * chip honestly is not there.
 */
export function DeltaChip({
  delta,
  deltaPct,
  currency,
}: {
  delta: number | null | undefined;
  deltaPct?: number | null | undefined;
  currency: string;
}) {
  if (delta === null || delta === undefined) return null;
  const cls = delta > 0 ? "delta up" : delta < 0 ? "delta down" : "delta flat";
  return (
    <span class={cls}>
      {signedMoney(delta, currency)}
      {deltaPct !== null && deltaPct !== undefined && (
        <span class="pct"> {signedPct(deltaPct)}</span>
      )}
    </span>
  );
}

/** A share-of-total bar: `share` is a 0..1 field from the file. */
export function ShareBar({ share, color }: { share: number; color?: string }) {
  return (
    <span class="bar-track wide">
      <span class="bar" style={{ width: `${share * 100}%`, background: color ?? "var(--accent)" }} />
    </span>
  );
}

export interface ChartPoint {
  /** The plotted value, verbatim from the file. */
  value: number;
  date: string;
  store: string;
}

/**
 * The price-history chart. Drawn at the container's real pixel width - never
 * stretched with `preserveAspectRatio="none"`, which is what distorts strokes
 * and turns end-point dots into blobs.
 *
 * The axis labels are the series' own min and max, and the tooltip shows a
 * point's own (date, store, price) - every number on this chart exists in
 * `summary.json` verbatim. The x axis is time-proportional, so three receipts
 * in one week and one a month later read as exactly that.
 */
export function PriceChart({ points, currency }: { points: ChartPoint[]; currency: string }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    const measure = () => setWidth(wrapRef.current?.clientWidth || 560);
    measure();
    addEventListener("resize", measure);
    return () => removeEventListener("resize", measure);
  }, []);

  if (!points.length) return null;

  const height = 140;
  const padL = 8;
  const padR = 52; /* newest point sits at the right edge; labels live there */
  const padT = 12;
  const padB = 24;
  const w = Math.max(width, 220);
  const innerW = w - padL - padR;
  const innerH = height - padT - padB;

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  const times = points.map((p) => Date.parse(p.date));
  const t0 = Math.min(...times);
  const t1 = Math.max(...times);
  const tSpan = t1 - t0 || 1;

  const x = (i: number) =>
    points.length === 1 ? padL + innerW / 2 : padL + ((times[i]! - t0) / tSpan) * innerW;
  const y = (v: number) => padT + (1 - (v - min) / span) * innerH;

  const line = points.map((p, i) => `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ");
  const flat = min === max;
  const current = active !== null ? points[active] : null;

  /** Nearest point to a pointer x, so hovering anywhere on the strip works. */
  const pick = (clientX: number) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = clientX - rect.left;
    let best = 0;
    for (let i = 1; i < points.length; i += 1) {
      if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i;
    }
    setActive(best);
  };

  return (
    <div
      ref={wrapRef}
      class="chart"
      onPointerMove={(e) => pick(e.clientX)}
      onPointerDown={(e) => pick(e.clientX)}
      onPointerLeave={() => setActive(null)}
    >
      <svg width={w} height={height} role="img" aria-label="price history">
        {/* Gridlines at the series' own extremes - values from the file. */}
        <line x1={padL} x2={w - padR + 6} y1={y(max)} y2={y(max)} class="grid" />
        {!flat && <line x1={padL} x2={w - padR + 6} y1={y(min)} y2={y(min)} class="grid" />}
        <text x={w - padR + 10} y={y(max) + 4} class="axis">
          {money(max, currency)}
        </text>
        {!flat && (
          <text x={w - padR + 10} y={y(min) + 4} class="axis">
            {money(min, currency)}
          </text>
        )}

        {points.length > 1 && (
          <polyline
            points={line}
            fill="none"
            stroke="var(--accent)"
            stroke-width="2"
            stroke-linejoin="round"
            stroke-linecap="round"
          />
        )}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={x(i)}
            cy={y(p.value)}
            r={active === i ? 5 : 3}
            class={active === i ? "pt on" : "pt"}
          />
        ))}
        {current && (
          <line x1={x(active!)} x2={x(active!)} y1={padT} y2={height - padB} class="guide" />
        )}

        <text x={padL} y={height - 6} class="axis">
          {dateName(points[0]!.date)}
        </text>
        {points.length > 1 && (
          <text x={w - padR} y={height - 6} class="axis end">
            {dateName(points[points.length - 1]!.date)}
          </text>
        )}
      </svg>

      {current && (
        <div
          class="tip"
          style={{
            left: `${Math.min(Math.max(x(active!), 60), w - 60)}px`,
          }}
        >
          <span class="num">{money(current.value, currency)}</span>
          <span class="muted">
            {dateName(current.date)} · {current.store}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Full-screen photo viewer. Tap anywhere (or Escape) to close. Receipts are
 * tall and thin, so `contain` with the whole viewport is the only way to
 * actually read one.
 */
export function Lightbox({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div class="lightbox" role="dialog" aria-label="Receipt photo" onClick={onClose}>
      <img src={url} alt="Receipt, full screen" />
      <button type="button" class="close" aria-label="Close">
        ×
      </button>
    </div>
  );
}

/**
 * One priced observation - latest, previous, cheapest, a by-store row. The
 * shape is the contract's price-point object, shown native-first (R13).
 */
export interface PricePointData {
  date: string;
  store: string;
  each: number;
  currency: string;
  qty?: number;
  deal?: string;
}

export function PricePoint({ point, title }: { point: PricePointData; title: string }) {
  return (
    <div class="pricepoint">
      <h4>{title}</h4>
      <p class="price">{money(point.each, point.currency)}</p>
      <p class="muted small">
        {point.store} · {point.date}
        {point.deal ? ` · ${point.deal}` : ""}
      </p>
    </div>
  );
}
